import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import {
    TextInput,
    NumberInput,
    Select,
    Textarea,
    Button,
    Group,
    Stack,
    Switch
} from '@mantine/core';
import { useCreateSubscription, useMyBusinesses } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import { Subscription, EMPTY_SUBSCRIPTION } from '@/types/subscription-models';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTIONS_PATH } from '@/lib/navigation';

interface ModalNewSubscriptionProps {
    opened?: boolean; // Controlled mode
    onClose: () => void;
    onCreate?: (subscription: Subscription) => void;
}

export default function ModalNewSubscription({ opened = true, onClose, onCreate }: ModalNewSubscriptionProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const createMutation = useCreateSubscription();
    const { data: businesses } = useMyBusinesses();

    const form = useForm<any>({
        initialValues: {
            ...EMPTY_SUBSCRIPTION,
            cycle: 'monthly',
            business_id: null as string | null, // Form needs string for Select
            auto_payment: false,
        },
        validate: {
            name: (value) => (value.length < 2 ? t('validation.name_too_short') : null),
            price: (value) => (value < 0 ? t('validation.price_negative') : null),
        },
    });

    const handleSubmit = (values: typeof form.values) => {
        createMutation.mutate({
            ...values,
            business_id: values.business_id ? Number(values.business_id) : null,
        }, {
            onSuccess: (data) => {
                notify.success(t('operation.success'), t('operation.add_recurring_expense.success'));
                onClose();
                navigate(SUBSCRIPTIONS_PATH);
            },
            onError: (error) => {
                notify.error(t('operation.error'), t('operation.add_recurring_expense.error'));
            }
        });
    };

    return (
        <div className="p-4">
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack>
                    <Select
                        label={t('recurring_expense_business')}
                        placeholder={t('select_business')}
                        data={businesses?.data?.map(b => ({ value: String(b.id), label: b.name })) || []}
                        searchable
                        clearable
                        value={form.values.business_id ? String(form.values.business_id) : null}
                        onChange={(value) => form.setFieldValue('business_id', value)}
                    />

                    <TextInput
                        required
                        label={t('name')}
                        placeholder={t('name')}
                        {...form.getInputProps('name')}
                    />

                    <Select
                        label={t('cycle')}
                        data={[
                            { value: 'monthly', label: t('cycle_monthly') },
                            { value: 'yearly', label: t('cycle_yearly') },
                        ]}
                        {...form.getInputProps('cycle')}
                    />

                    <NumberInput
                        label={t('due_day')}
                        placeholder="1-31"
                        min={1}
                        max={31}
                        {...form.getInputProps('due_day')}
                    />

                    <Group grow>
                        <NumberInput
                            label={t('price')}
                            min={0}
                            hideControls
                            {...form.getInputProps('price')}
                        />
                        <Select
                            label={t('currency')}
                            data={Object.entries(t('currencies', { returnObjects: true }) as Record<string, string>).map(([value, label]) => ({ value, label }))}
                            {...form.getInputProps('currency')}
                        />
                    </Group>

                    <Select
                        label={t('status')}
                        data={[
                            { value: 'active', label: t('status_active') },
                            { value: 'paused', label: t('status_paused') },
                            { value: 'canceled', label: t('status_canceled') },
                        ]}
                        {...form.getInputProps('status')}
                    />

                    <Switch
                        label={t('auto_payment')}
                        checked={form.values.auto_payment}
                        onChange={(e) => form.setFieldValue('auto_payment', e.currentTarget.checked)}
                        className="font-semibold text-left"
                    />

                    <Textarea
                        label={t('notes')}
                        placeholder={t('notes')}
                        minRows={3}
                        {...form.getInputProps('description')}
                    />

                    <div className="flex justify-between mt-4">
                        <Button
                            size="xs"
                            color="black"
                            variant='subtle'
                            onClick={onClose}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            size="xs"
                            color='black'
                            type="submit"
                            loading={createMutation.isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </Stack>
            </form>
        </div>
    );
}
